/**
 * CapacitorService - Mobile App Integration Layer
 * 
 * Handles all Capacitor-specific functionality for the Aprendos mobile app
 * including background audio, push notifications, filesystem operations,
 * and app lifecycle management.
 */

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { PushNotifications } from '@capacitor/push-notifications';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Media } from '@capacitor-community/media';
import { auth, db } from '../firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

class CapacitorService {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.platform = Capacitor.getPlatform();
    this.initialized = false;
    
    // Audio state
    this.currentAudio = null;
    this.audioQueue = [];
    this.isPlaying = false;
    
    console.log(`CapacitorService initialized - Native: ${this.isNative}, Platform: ${this.platform}`);
  }

  /**
   * Initialize all mobile services
   */
  async initialize() {
    if (this.initialized) return;

    try {
      if (this.isNative) {
        await this.initializeApp();
        await this.initializePushNotifications();
        await this.initializeAudio();
        await this.initializeFilesystem();
      }
      
      this.initialized = true;
      console.log('CapacitorService fully initialized');
    } catch (error) {
      console.error('Failed to initialize CapacitorService:', error);
    }
  }

  /**
   * Initialize app lifecycle listeners
   */
  async initializeApp() {
    App.addListener('appStateChange', ({ isActive }) => {
      console.log('App state changed. Is active:', isActive);
      
      if (isActive) {
        // App came to foreground
        this.onAppActive();
      } else {
        // App went to background
        this.onAppBackground();
      }
    });

    App.addListener('appUrlOpen', (event) => {
      console.log('App opened with URL:', event.url);
      // Handle deep links
    });

    App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        App.exitApp();
      }
    });
  }

  /**
   * Initialize push notifications
   */
  async initializePushNotifications() {
    // Request permissions
    const result = await PushNotifications.requestPermissions();
    
    if (result.receive === 'granted') {
      // Register for push notifications
      await PushNotifications.register();
      
      // Add listeners
      PushNotifications.addListener('registration', (token) => {
        console.log('Push registration token:', token.value);
        // Send token to Firebase
        this.sendTokenToFirebase(token.value);
      });

      PushNotifications.addListener('registrationError', (error) => {
        console.error('Push registration error:', error);
      });

      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('Push notification received:', notification);
        // Handle foreground notifications
      });

      PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
        console.log('Push notification action performed:', notification);
        // Handle notification taps
        this.handleNotificationAction(notification);
      });
    }
  }

  /**
   * Initialize background audio capabilities
   */
  async initializeAudio() {
    // Set up media session for background playback
    if ('mediaSession' in navigator) {
      navigator.mediaSession.setActionHandler('play', () => {
        this.resumeAudio();
      });

      navigator.mediaSession.setActionHandler('pause', () => {
        this.pauseAudio();
      });

      navigator.mediaSession.setActionHandler('previoustrack', () => {
        this.previousTrack();
      });

      navigator.mediaSession.setActionHandler('nexttrack', () => {
        this.nextTrack();
      });
    }
  }

  /**
   * Initialize filesystem for offline content
   */
  async initializeFilesystem() {
    try {
      // Create audio cache directory
      await Filesystem.mkdir({
        path: 'audio_cache',
        directory: Directory.Data,
        recursive: true
      });

      // Create exercise cache directory
      await Filesystem.mkdir({
        path: 'exercise_cache',
        directory: Directory.Data,
        recursive: true
      });

      console.log('Filesystem directories created successfully');
    } catch (error) {
      console.log('Filesystem directories may already exist:', error);
    }
  }

  /**
   * Background Audio Methods
   */
  
  async playAudio(audioUrl, metadata = {}) {
    if (!this.isNative) {
      // Fallback to web audio
      return this.playWebAudio(audioUrl);
    }

    try {
      // Set media metadata for lock screen controls
      if ('mediaSession' in navigator) {
        const defaultArt = '/new-favicon-plain/web-app-manifest-512x512.png';
        navigator.mediaSession.metadata = new MediaMetadata({
          title: metadata.title || 'Aprendos Lesson',
          artist: metadata.artist || 'Aprendos',
          album: metadata.album || 'Language Learning',
          artwork: metadata.artwork || [
            { src: defaultArt, sizes: '512x512', type: 'image/png' }
          ]
        });
      }

      // Use Capacitor Media plugin for background playback
      // Compute absolute cover URL for native lock screen controls
      let coverAbs = null;
      let coverData = null;
      try {
        const art = (metadata?.artwork && metadata.artwork[0]?.src) || '/new-favicon-plain/web-app-manifest-512x512.png';
        coverAbs = /^https?:\/\//i.test(art)
          ? art
          : new URL(art, (typeof window !== 'undefined' ? window.location.origin : 'http://localhost')).href;
        // Try to load and embed as data URL for better iOS lock-screen reliability
        try {
          const resp = await fetch(coverAbs);
          const blob = await resp.blob();
          const base64 = await this.blobToBase64(blob);
          coverData = `data:${blob.type};base64,${base64}`;
        } catch {
          void 0;
        }
      } catch {
        void 0;
      }

      await Media.playAudio({
        url: audioUrl,
        title: metadata.title,
        artist: metadata.artist,
        album: metadata.album,
        // Prefer embedded data URL if available; fall back to absolute URL
        cover: coverData || coverAbs,
        artworkUrl: coverAbs
      });

      this.isPlaying = true;
      this.currentAudio = { url: audioUrl, metadata };
      
      return true;
    } catch (error) {
      console.error('Failed to play audio:', error);
      return false;
    }
  }

  async pauseAudio() {
    if (this.isNative) {
      await Media.pauseAudio();
    }
    this.isPlaying = false;
  }

  async resumeAudio() {
    if (this.isNative) {
      await Media.resumeAudio();
    }
    this.isPlaying = true;
  }

  async stopAudio() {
    if (this.isNative) {
      await Media.stopAudio();
    }
    this.isPlaying = false;
    this.currentAudio = null;
  }

  /**
   * Offline Content Management
   */
  
  async cacheAudioFile(audioUrl, fileName) {
    if (!this.isNative) return audioUrl;

    try {
      // Download audio file
      const response = await fetch(audioUrl);
      const blob = await response.blob();
      const base64Data = await this.blobToBase64(blob);

      // Save to filesystem
      await Filesystem.writeFile({
        path: `audio_cache/${fileName}`,
        data: base64Data,
        directory: Directory.Data
      });

      return `audio_cache/${fileName}`;
    } catch (error) {
      console.error('Failed to cache audio file:', error);
      return audioUrl;
    }
  }

  async getCachedAudioPath(fileName) {
    if (!this.isNative) return null;

    try {
      const result = await Filesystem.readFile({
        path: `audio_cache/${fileName}`,
        directory: Directory.Data
      });

      return result.uri;
    } catch (error) {
      console.error('Cached audio file not found:', error);
      return null;
    }
  }

  async clearAudioCache() {
    if (!this.isNative) return;

    try {
      await Filesystem.rmdir({
        path: 'audio_cache',
        directory: Directory.Data,
        recursive: true
      });

      await Filesystem.mkdir({
        path: 'audio_cache',
        directory: Directory.Data,
        recursive: true
      });
    } catch (error) {
      console.error('Failed to clear audio cache:', error);
    }
  }

  /**
   * Learning Analytics & Notifications
   */
  
  async scheduleReminderNotification(title, body, delay) {
    if (!this.isNative) return;

    // Schedule local notification for learning reminder
    // This would integrate with a local notification plugin
    console.log('Scheduling reminder:', { title, body, delay });
  }

  async sendLearningProgress(progressData) {
    if (!this.isNative) return;

    // Send learning progress to Firebase Analytics
    // This would integrate with Firebase Analytics plugin
    console.log('Sending learning progress:', progressData);
  }

  /**
   * Utility Methods
   */

  onAppActive() {
    console.log('App became active - resuming learning session');
    // Sync with Firebase, check for updates
  }

  onAppBackground() {
    console.log('App went to background - maintaining audio if needed');
    // Continue background audio, save state
  }

  async sendTokenToFirebase(token) {
    try {
      const user = auth?.currentUser;
      if (!user) {
        console.warn('No authenticated user; skipping device token persistence');
        return;
      }
      // Stable device id in localStorage
      let deviceId = null;
      try {
        deviceId = localStorage.getItem('device-id');
        if (!deviceId) {
          deviceId = `dev_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
          localStorage.setItem('device-id', deviceId);
        }
      } catch {
        void 0;
      }

      const ref = doc(db, 'users', user.uid, 'devices', deviceId || 'unknown');
      await setDoc(ref, {
        token,
        platform: this.platform,
        isNative: this.isNative,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      console.log('Device token persisted');
    } catch (e) {
      console.error('Failed to persist device token:', e);
    }
  }

  handleNotificationAction(notification) {
    // Handle notification taps - navigate to appropriate screen
    console.log('Handling notification action:', notification);
  }

  async blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  playWebAudio(audioUrl) {
    // Fallback web audio implementation
    const audio = new Audio(audioUrl);
    audio.play();
    return Promise.resolve(true);
  }

  /**
   * Platform Detection
   */
  
  isIOS() {
    return this.platform === 'ios';
  }

  isAndroid() {
    return this.platform === 'android';
  }

  isWeb() {
    return this.platform === 'web';
  }
}

// Export singleton instance
export const capacitorService = new CapacitorService();
export default CapacitorService;
